import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  FormControl,
  FormHelperText,
  InputBaseComponentProps,
  InputLabel,
  OutlinedInput,
  SxProps,
  useTheme,
  Box,
  Typography,
  Popper,
  Fade,
  Paper,
  IconButton,
  alpha,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import { INTEGER_REGEXP, NUMBER_REGEXP } from '../../constants/regEx';

export interface TextFieldProps {
  placeholder?: string;
  label?: string;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  className?: string;
  inputClassName?: string;
  type?: 'number' | 'integer' | 'string' | 'password' | 'text';
  style?: React.CSSProperties;
  disabled?: boolean;
  value?: string | number;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onBlur?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  error?: string;
  name?: string;
  multiline?: boolean;
  required?: boolean;
  number?: boolean;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  inputRef?: React.Ref<any>;
  sx?: SxProps;
  rows?: number;
  showInputProps?: boolean;
  maxLength?: number;
  helperText?: string;
  showCharCount?: boolean;
  validationState?: {
    loading?: boolean;
    valid?: boolean | null;
    suggestion?: string | null;
    suggestions?: string[];
    error?: string | null;
  };
  onApplySuggestion?: (suggestion: string) => void;
}

function SbTextField(props: TextFieldProps) {
  const { type, showInputProps = true, maxLength, value = '', showCharCount } = props;
  const [charCount, setCharCount] = useState<number | undefined>(value ? String(value).length : undefined);
  const theme = useTheme();

  const [isBadgeHovered, setIsBadgeHovered] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const lastStateSigRef = useRef<string>('');

  const loading = props.validationState?.loading;
  const valid = props.validationState?.valid;

  // Reset dismissal whenever a new validation result arrives or validation state changes
  useEffect(() => {
    const currentStateSig = JSON.stringify({
      loading: props.validationState?.loading,
      valid: props.validationState?.valid,
      suggestion: props.validationState?.suggestion,
      suggestions: props.validationState?.suggestions,
      error: props.validationState?.error,
    });

    if (currentStateSig !== lastStateSigRef.current) {
      lastStateSigRef.current = currentStateSig;
      setIsDismissed(false);
    }
  }, [
    props.validationState?.loading,
    props.validationState?.valid,
    props.validationState?.suggestion,
    props.validationState?.suggestions,
    props.validationState?.error,
  ]);

  useEffect(() => {
    if (value) {
      setCharCount(String(value).length);
    } else {
      setCharCount(undefined);
    }
  }, [value]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (props.onChange) {
      if (type === 'number' || type === 'integer') {
        const regExp = type === 'number' ? NUMBER_REGEXP : INTEGER_REGEXP;
        if (regExp.test(event.target.value) || event.target.value === '') {
          props.onChange(event);
          setCharCount(value.length);
        }
      } else {
        props.onChange(event);
        setCharCount(value.length);
      }
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (type === 'number' || type === 'integer') {
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const inputProps: InputBaseComponentProps = {
    ...(showInputProps && type === 'number' ? { type: 'number', step: '0.01', min: '1' } : {}),
    ...(maxLength ? { maxLength: maxLength } : {}),
  };

  const hasFrontendError = !!props.error;
  const hasAiError = !!props.validationState?.error;
  const hasError = hasFrontendError || hasAiError;

  const isInvalid = valid === false && (!!props.validationState?.suggestion || hasAiError);

  // Popover only opens when invalid (valid === false), unless dismissed.
  // If dismissed, hovering the AI badge chip reactivates it.
  const isPopoverOpen = isInvalid && (!isDismissed || isBadgeHovered);

  const borderStyle = (() => {
    if (loading) {
      return {
        border: `1px solid ${theme.palette.primary.main}`,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.12)}`,
      };
    }
    if (valid === false && props.validationState?.suggestion) {
      return {
        border: `1px solid ${theme.palette.warning.main}`,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.warning.main, 0.12)}`,
      };
    }
    return {
      border: hasError ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`,
    };
  })();

  const paperStyle = useMemo(() => {
    return {
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
      border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
      background: theme.palette.mode === 'dark' ? '#271717' : '#FFF8F8',
    };
  }, [theme.palette.error.main, theme.palette.mode]);

  const tooltipInnerContent = useMemo(() => {
    if (valid !== false) {
      return null;
    }

    const suggestedValue = props.validationState?.suggestion;
    const rawSuggestions = props.validationState?.suggestions;
    const rawError = props.validationState?.error;

    const suggestionsList =
      rawSuggestions && rawSuggestions.length > 0
        ? rawSuggestions
        : rawError
        ? rawError.split('\n').filter(Boolean)
        : [];

    const mainMessage = suggestedValue
      ? rawError || `This ${props.label || 'value'} doesn't match — did you mean ${suggestedValue}?`
      : suggestionsList.length === 1
      ? suggestionsList[0]
      : suggestionsList.length > 1
      ? `Please review these details.`
      : `This value couldn't be verified by AI.`;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Top Header Row: Attention Required & Close Button on Top-Right */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              backgroundColor: alpha(theme.palette.error.main, 0.08),
              px: '8px',
              py: '2px',
              borderRadius: '4px',
            }}
          >
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: theme.palette.error.main }}>
              Attention Required
            </Typography>
          </Box>

          <IconButton
            size="small"
            aria-label="Dismiss AI validation"
            onClick={e => {
              e.stopPropagation();
              setIsBadgeHovered(false);
              setIsDismissed(true);
            }}
            sx={{
              p: '2px',
              color: 'text.secondary',
              opacity: 0.7,
              '&:hover': { opacity: 1, backgroundColor: 'rgba(0, 0, 0, 0.06)' },
            }}
          >
            <CloseIcon sx={{ fontSize: '0.85rem' }} />
          </IconButton>
        </Box>

        {/* Content Row: Theme-Based Sparkle Icon + Validation Text & Bullets */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.25 }}>
          <AutoAwesomeIcon
            sx={{
              fontSize: '1rem',
              color: theme.palette.error.main,
              mt: '2px',
              flexShrink: 0,
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.4 }}>
              {mainMessage}
            </Typography>

            {suggestionsList.length > 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
                {suggestionsList.map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: theme.palette.error.main, lineHeight: 1.4 }}>
                      •
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.4 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {suggestedValue && props.onApplySuggestion && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <button
                  type="button"
                  aria-label={`Use ${suggestedValue}`}
                  onClick={() => {
                    if (props.onApplySuggestion) {
                      props.onApplySuggestion(suggestedValue);
                    }
                  }}
                  style={{
                    backgroundColor: theme.palette.error.main,
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '16px',
                    padding: '4px 14px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  Use {suggestedValue}
                </button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }, [
    valid,
    props.validationState?.suggestion,
    props.validationState?.suggestions,
    props.validationState?.error,
    props.onApplySuggestion,
    props.label,
    theme.palette.error.main,
  ]);

  return (
    <div ref={containerRef} style={props.style || {}} className={`sb-textfield-wrapper ${props.className || ''}`}>
      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .sb-textfield-input, .MuiOutlinedInput-root, * {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      {props.label && (
        <InputLabel
          className="sb-textfield-label"
          required={props.required}
          sx={{
            fontWeight: 500,
            fontSize: '0.875rem',
            marginBottom: '8px',
            display: 'block',
            letterSpacing: '0.3px',
            transition: 'color 0.2s ease',
            '&.Mui-focused': {
              color: hasError ? theme.palette.error.main : theme.palette.primary.main,
            },
          }}
        >
          {props.label}
        </InputLabel>
      )}
      <FormControl className={`sb-textfield-control ${props.inputClassName || ''}`} fullWidth error={hasError}>
        <OutlinedInput
          multiline={props.multiline}
          rows={props.rows}
          error={hasError}
          name={props.name}
          type={props.type === 'number' ? 'string' : props.type}
          className="sb-textfield-input"
          placeholder={props.placeholder || ''}
          endAdornment={
            <React.Fragment>
              {loading && (
                <Box
                  onMouseEnter={() => setIsBadgeHovered(true)}
                  onMouseLeave={() => setIsBadgeHovered(false)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: theme.palette.primary.main,
                    mr: 1,
                    cursor: 'help',
                    flexShrink: 0,
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: '1.05rem' }} />
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                    AI
                  </span>
                </Box>
              )}
              {/* Only show AI Indicator if validation has run and failed (valid === false) */}
              {!loading && valid === false && (props.validationState?.suggestion || hasAiError) && (
                <Box
                  onMouseEnter={() => setIsBadgeHovered(true)}
                  onMouseLeave={() => setIsBadgeHovered(false)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: props.validationState?.suggestion ? theme.palette.warning.main : theme.palette.error.main,
                    mr: 1,
                    cursor: 'help',
                    animation: 'fadeInScale 0.2s ease',
                    flexShrink: 0,
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: '0.9rem', opacity: 0.9 }} />
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                    {props.validationState?.suggestion ? 'AI' : 'AI Alert'}
                  </span>
                </Box>
              )}
              {props.endAdornment || <></>}
            </React.Fragment>
          }
          startAdornment={props.startAdornment || <></>}
          sx={{
            ...props.sx,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.7)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },

            border: borderStyle.border,
            boxShadow: borderStyle.boxShadow || 'none',

            '& .MuiOutlinedInput-input': {
              fontSize: '0.95rem',
              fontWeight: 400,
              color: theme.palette.text.primary,
              padding: '12px 16px',
              letterSpacing: '0.3px',
              transition: 'all 0.2s ease',

              '&::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 0.6,
              },

              '&:autofill, &:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              },
            },

            '&.Mui-focused': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.85)' : 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(12px)',
              border: borderStyle.border,
              boxShadow: loading
                ? borderStyle.boxShadow
                : valid === false && props.validationState?.suggestion
                ? `0 0 0 2px ${alpha(theme.palette.warning.main, 0.2)}`
                : hasError
                ? `0 0 0 2px ${alpha(theme.palette.error.main, 0.2)}`
                : `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
            },

            '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.8)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(12px)',
              borderColor: loading
                ? theme.palette.primary.main
                : valid === false && props.validationState?.suggestion
                ? theme.palette.warning.main
                : hasError
                ? theme.palette.error.main
                : theme.palette.primary.main,
              border: borderStyle.border,
              boxShadow: borderStyle.boxShadow || 'none',
            },

            '&.Mui-disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              backdropFilter: 'blur(8px)',
              borderColor: theme.palette.divider,
              cursor: 'not-allowed',

              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.disabled,
                WebkitTextFillColor: theme.palette.text.disabled,
                cursor: 'not-allowed',
              },
            },

            '&.MuiOutlinedInput-multiline': {
              padding: 0,

              '& .MuiOutlinedInput-input': {
                padding: '12px 16px',
              },
            },

            '& .MuiInputAdornment-root': {
              color: theme.palette.text.secondary,
              transition: 'color 0.2s ease',
            },

            '&.Mui-focused .MuiInputAdornment-root': {
              color: theme.palette.primary.main,
            },
          }}
          disabled={!!props.disabled}
          value={props.value || ''}
          onChange={onChange}
          inputRef={props.inputRef}
          onFocus={handleFocus}
          onBlur={handleBlur}
          required={props.required}
          onPaste={props.onPaste}
          inputProps={inputProps}
          onClick={props.onClick}
          onKeyDown={props.onKeyDown}
          onWheel={handleWheel}
        />

        {/* Frontend Validation Error Message */}
        {hasFrontendError && (
          <FormHelperText
            error
            sx={{
              margin: '6px 4px 0 4px',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: theme.palette.error.main,
              letterSpacing: '0.2px',
              animation: 'slideInDown 0.2s ease',

              '@keyframes slideInDown': {
                from: {
                  opacity: 0,
                  transform: 'translateY(-4px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {typeof props.error === 'string' ? props.error : 'Invalid Data'}
          </FormHelperText>
        )}

        {/* Character Count */}
        {showCharCount && charCount !== undefined && (
          <div
            style={{
              textAlign: 'right',
              marginTop: '6px',
              fontSize: '0.7rem',
              fontWeight: 500,
              color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
              letterSpacing: '0.2px',
              transition: 'color 0.2s ease',
            }}
          >
            {charCount}
            {maxLength ? `/${maxLength}` : ''}
          </div>
        )}
      </FormControl>

      {/* Floating Premium AI Tooltip/Popover */}
      <Popper
        open={isPopoverOpen}
        anchorEl={containerRef.current}
        placement="bottom-start"
        transition
        disablePortal={false}
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              padding: 8,
            },
          },
        ]}
        style={{ zIndex: 990, width: containerRef.current ? containerRef.current.clientWidth : 320, maxWidth: 360 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={isDismissed ? 0 : { enter: 150, exit: 0 }}>
            <Paper
              sx={{
                mt: 1,
                p: 2,
                borderRadius: '16px',
                position: 'relative',
                overflow: 'hidden',
                ...paperStyle,
              }}
            >
              {tooltipInnerContent}
            </Paper>
          </Fade>
        )}
      </Popper>
    </div>
  );
}

export default SbTextField;
